export interface FormData {
  name: string;
  phone: string;
  email: string;
  message: string;
}

export interface FormErrors {
  [key: string]: string | undefined;
  name?: string;
  phone?: string;
  email?: string;
  message?: string;
}

export type TouchedFields = {
  [K in keyof FormData]: boolean;
};

export type Language = "ko" | "en";

const errorMessages = {
  ko: {
    name: {
      required: "이름을 입력해주세요.",
      tooShortKorean: "한글 이름은 최소 2글자 이상이어야 합니다.",
      tooShortEnglish: "영문 이름은 최소 3글자 이상이어야 합니다.",
      invalidFormat: "한글 또는 영문으로만 입력해주세요.",
    },
    phone: {
      required: "전화번호를 입력해주세요.",
      invalidFormat: "숫자와 +, - 기호만 입력 가능합니다.",
      tooLong: "전화번호는 20자 이하여야 합니다.",
    },
    email: {
      required: "이메일을 입력해주세요.",
      invalidFormat: "유효한 이메일 주소를 입력해주세요.",
    },
    message: {
      required: "내용을 입력해주세요.",
      tooShort: "내용은 최소 15자 이상이어야 합니다.",
    },
  },
  en: {
    name: {
      required: "Please enter your name.",
      tooShortKorean: "Korean names must be at least 2 characters long.",
      tooShortEnglish: "English names must be at least 3 characters long.",
      invalidFormat: "Please enter your name in Korean or English only.",
    },
    phone: {
      required: "Please enter your phone number.",
      invalidFormat: "Only numbers and +, - symbols are allowed.",
      tooLong: "Phone number must be 20 characters or less.",
    },
    email: {
      required: "Please enter your email address.",
      invalidFormat: "Please enter a valid email address.",
    },
    message: {
      required: "Please enter your message.",
      tooShort: "Message must be at least 15 characters long.",
    },
  },
};

const checkNameValidity = (
  value: string,
  language: Language
): string | undefined => {
  if (!value) return errorMessages[language].name.required;

  const isKorean = /^[가-힣]+$/.test(value);
  const isEnglish = /^[a-zA-Z\s]+$/.test(value);

  if (isKorean && value.length < 2)
    return errorMessages[language].name.tooShortKorean;
  if (isEnglish && value.length < 3)
    return errorMessages[language].name.tooShortEnglish;
  if (!isKorean && !isEnglish)
    return errorMessages[language].name.invalidFormat;
  return undefined;
};

export const validateField = (
  name: keyof FormData,
  value: string,
  language: Language = "ko"
): string | undefined => {
  switch (name) {
    case "name":
      return checkNameValidity(value, language);

    case "phone":
      if (!value) return errorMessages[language].phone.required;
      if (!/^[\d+-]+$/.test(value))
        return errorMessages[language].phone.invalidFormat;
      if (value.length > 20) return errorMessages[language].phone.tooLong;
      return undefined;

    case "email":
      if (!value) return errorMessages[language].email.required;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
        return errorMessages[language].email.invalidFormat;
      return undefined;

    case "message":
      if (!value) return errorMessages[language].message.required;
      if (value.length < 15) return errorMessages[language].message.tooShort;
      return undefined;

    default:
      return undefined;
  }
};

export const validateForm = (
  formData: FormData,
  language: Language = "ko"
): FormErrors => {
  const errors: FormErrors = {};

  (Object.keys(formData) as Array<keyof FormData>).forEach((key) => {
    const error = validateField(key, formData[key], language);
    if (error) {
      errors[key] = error;
    }
  });

  return errors;
};
